import {
  ACCEPT_TERMS,
  COLLECT_MOVE_IN_COSTS,
  EDIT_ACCOUNT_INFO,
  EDIT_DUE_DATE,
  EDIT_INVITE_TENANT,
  EDIT_MOVE_IN_COSTS,
  EDIT_PAYMENT_SETTINGS,
  EDIT_PAYMENT_TERM,
  EDIT_RENT_AMOUNT,
  EDIT_TAX_VERIFICATION,
  REQUEST_EMBEDDED_COVERAGE_REQUEST_URL,
  REQUEST_GET_ID_VERIFICATION_INFO,
  REQUEST_LANDLORD_STATUS,
  REQUEST_MARK_COVERAGE_REQUEST_SIGNED,
  REQUEST_RENTAL_PROTECTION_LEGAL_DISCLAIMER,
  REQUEST_SAVE_RENTAL_RELATIONSHIP_ID_TO_POLICY,
  SAVE_ACCOUNT_INFO,
  SAVE_ENCODED_LEASE_ID,
  SAVE_EXPERIAN_ERROR_STATE,
  SAVE_IDENTITY_VERIFICATION,
  SAVE_INVITE_TENANT,
  SAVE_LEASE_ID,
  SAVE_MOVE_IN_COST_SETTINGS,
  SAVE_PAYMENT_SETTINGS,
  SAVE_RENTAL_PROTECTION_FEATURE,
  START_NEW_TERM,
} from '@rental-manager/constants/ActionConstants';
import REQUEST_STATUS from '@common/constants/requestStatus';

const defaultOnboardingValues = {
  acceptTerms: false,
  encodedLeaseId: '',
  collectMoveInCosts: false,
  moveInCostsSettings: {
    securityDepositSettings: {
      securityDepositPaymentAmount: 0,
      securityDepositNotes: '',
    },
    proratedRentSettings: {
      proratedRentPaymentAmount: 0,
      proratedRentNotes: '',
    },
    otherSettings: [],
    moveInCostsPaymentLimits: 1000000,
    dueDate: '',
    editMode: false,
    term: 'current',
  },
  paymentSettings: {
    /* Override for paymentDayOfTheMonth when editMode=true */
    editDayOfTheMonth: null,
    editMode: false,
    newTerm: false,
    monthlyRent: '',
    firstDueDate: '',
    paymentEndDate: '',
    paymentDayOfTheMonth: 1,
    numberOfPayments: 12,
    monthToMonth: false,
    paymentEndDateInteracted: false,
    requestStatus: null,
  },
  accountInfo: {
    editMode: false,
    cardLast4: '',
    cardBrand: '',
    paymentType: 'plaid',
    plaidBankName: '',
    routingNumber: '',
    checkingAccountNumber: '',
  },
  identityVerificationInfo: {
    editMode: false,
    idPageAccessed: false,
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    phoneNumber: '',
    monthOfBirth: '',
    dayOfBirth: '',
    yearOfBirth: '',
    lastFourDigitsOfSSN: '',
    fullSocialSecurityNumber: '',
    businessName: '',
    dbaName: '',
    employerIdentificationNumber: '',
    businessStreet: '',
    businessCity: '',
    businessState: '',
    businessZipCode: '',
    selectedTaxVerificationType: '',
  },
  tenantInvitationInfo: {
    editMode: false,
    tenantFirstName: '',
    tenantLastName: '',
    emailAddress: '',
  },
  leasesForProperty: [{}],
  getLandlordStatusRequestStatus: null,
  getLandlordStatusMessageCode: null,
  rentalProtectionDisclaimers: [{}],
  zDocsCoverageRequestUrl: '',
  identityCheckResponse: '',
  rentGuarantee: false,
};

const initState = {};

const onboardingReducer = (state = initState, action) => {
  switch (action.type) {
    case REQUEST_LANDLORD_STATUS.SUCCESS:
      return state[action.meta.propertyId]
        ? { ...state } // don't overwrite saved values if they already exist
        : {
            ...state,
            [action.meta.propertyId]: {
              ...defaultOnboardingValues,
              leasesForProperty: action.payload.data.leasesForProperty,
            },
          };
    case REQUEST_LANDLORD_STATUS.FAIL:
      return state[action.meta.propertyId]
        ? { ...state } // don't overwrite saved values if they already exist
        : {
            ...state,
            [action.meta.propertyId]: {
              ...defaultOnboardingValues,
              getLandlordStatusRequestStatus: REQUEST_STATUS.FAIL,
              getLandlordStatusMessageCode: action?.payload?.response?.messageCode,
            },
          };
    case REQUEST_EMBEDDED_COVERAGE_REQUEST_URL.SUCCESS:
      return {
        ...state,
        [action.meta.propertyId]: {
          ...state[action.meta.propertyId],
          zDocsCoverageRequestUrl: action.payload.data.url,
          rentalProtectionQuoteId: action.payload.data.quoteId,
        },
      };
    case REQUEST_RENTAL_PROTECTION_LEGAL_DISCLAIMER.SUCCESS:
      return {
        ...state,
        [action.meta.propertyId]: {
          ...state[action.meta.propertyId],
          rentalProtectionDisclaimers: action.payload.data.disclaimers,
        },
      };
    case REQUEST_SAVE_RENTAL_RELATIONSHIP_ID_TO_POLICY.SUCCESS:
      return {
        ...state,
        [action.meta.propertyId]: {
          ...state[action.meta.propertyId],
          rentalProtectionPolicy: action.payload.data,
        },
      };
    case REQUEST_MARK_COVERAGE_REQUEST_SIGNED.SUCCESS:
      return {
        ...state,
        [action.meta.propertyId]: {
          ...state[action.meta.propertyId],
          rentalProtectionPolicy: {
            ...state[action.meta.propertyId].rentalProtectionPolicy,
            coverageRequestSigned: true,
          },
        },
      };
    case REQUEST_GET_ID_VERIFICATION_INFO.INIT:
      return {
        ...state,
        [action.meta.propertyId]: {
          ...state[action.meta.propertyId],
          identityVerificationInfo: {
            ...state[action.meta.propertyId].identityVerificationInfo,
            requestStatus: REQUEST_STATUS.INIT,
          },
        },
      };
    case REQUEST_GET_ID_VERIFICATION_INFO.SUCCESS:
      return {
        ...state,
        [action.meta.propertyId]: {
          ...state[action.meta.propertyId],
          identityVerificationInfo: {
            ...state[action.meta.propertyId].identityVerificationInfo,
            ...action.payload.data,
            requestStatus: REQUEST_STATUS.SUCCESS,
          },
        },
      };
    case REQUEST_GET_ID_VERIFICATION_INFO.FAIL:
      return {
        ...state,
        [action.meta.propertyId]: {
          ...state[action.meta.propertyId],
          identityVerificationInfo: {
            ...state[action.meta.propertyId].identityVerificationInfo,
            requestStatus: REQUEST_STATUS.FAIL,
          },
        },
      };
    case ACCEPT_TERMS:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          acceptTerms: true,
        },
      };
    case COLLECT_MOVE_IN_COSTS:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          collectMoveInCosts: true,
        },
      };
    case SAVE_PAYMENT_SETTINGS:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          paymentSettings: {
            ...state[action.propertyId].paymentSettings,
            ...action.paymentSettings,
          },
        },
      };
    case SAVE_MOVE_IN_COST_SETTINGS:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          moveInCostsSettings: {
            ...state[action.propertyId].moveInCostsSettings,
            ...action.payload,
          },
        },
      };
    case SAVE_ACCOUNT_INFO:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          accountInfo: {
            ...state[action.propertyId].accountInfo,
            ...action.accountInfo,
          },
        },
      };
    case SAVE_IDENTITY_VERIFICATION:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          identityVerificationInfo: {
            ...state[action.propertyId].identityVerificationInfo,
            ...action.identityVerificationInfo,
          },
        },
      };
    case SAVE_EXPERIAN_ERROR_STATE:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          identityCheckResponse: action.identityCheckResponse,
        },
      };
    case SAVE_INVITE_TENANT:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          tenantInvitationInfo: {
            ...state[action.propertyId].tenantInvitationInfo,
            ...action.tenantInvitationInfo,
          },
        },
      };
    case EDIT_PAYMENT_SETTINGS:
    case EDIT_RENT_AMOUNT:
    case EDIT_DUE_DATE:
    case EDIT_PAYMENT_TERM:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          paymentSettings: {
            ...state[action.propertyId].paymentSettings,
            editMode: true,
          },
        },
      };
    case EDIT_MOVE_IN_COSTS:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          moveInCostsSettings: {
            ...state[action.propertyId].moveInCostsSettings,
            editMode: true,
          },
        },
      };
    case EDIT_ACCOUNT_INFO:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          accountInfo: {
            ...state[action.propertyId].accountInfo,
            editMode: true,
          },
        },
      };
    case EDIT_TAX_VERIFICATION:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          identityVerificationInfo: {
            ...state[action.propertyId].identityVerificationInfo,
            editMode: true,
          },
        },
      };
    case EDIT_INVITE_TENANT:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          tenantInvitationInfo: {
            ...state[action.propertyId].tenantInvitationInfo,
            editMode: true,
          },
        },
      };
    case START_NEW_TERM:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          paymentSettings: {
            ...state[action.propertyId].paymentSettings,
            newTerm: true,
          },
        },
      };
    case SAVE_LEASE_ID:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          leaseId: action.leaseId,
          paymentSettings: {
            ...state[action.propertyId].paymentSettings,
            requestStatus: REQUEST_STATUS.SUCCESS,
          },
        },
      };
    case SAVE_RENTAL_PROTECTION_FEATURE:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          rentGuarantee: action.rentGuarantee,
        },
      };
    case SAVE_ENCODED_LEASE_ID:
      return {
        ...state,
        [action.propertyId]: {
          ...state[action.propertyId],
          encodedLeaseId: action.encodedLeaseId,
        },
      };
    default:
      return state;
  }
};

export default onboardingReducer;
